import type { ReactNode } from 'react';

import { Image } from '@utilities/Image';
import { Link } from '@utilities/Link';

import { cn } from '../../../../../utils';
import { ActivityStatus } from '../../../ActivityStatus';
import { Card } from '../../../Card';
import styles from './HotOfferCard.module.scss';
import { TokenRewardChip } from './TokenRewardChip';

type StatusType = 'available' | 'active' | 'underReview' | 'claimed' | 'ended' | 'paused';

const getStatus = (
  isClaimed: boolean | null,
  isEnded: boolean,
  isPaused?: boolean | null,
  claimsLimit?: number | null,
  spotsLeft?: number,
): StatusType => {
  if (isClaimed) {
    return 'claimed';
  }

  if (isEnded || spotsLeft === 0) {
    return 'ended';
  }

  if (isPaused) {
    return 'paused';
  }

  if (!claimsLimit) {
    return 'active';
  }

  if (isPaused || spotsLeft === 0) {
    return 'paused';
  }

  return 'available';
};

interface HotOfferCardProps {
  offer: {
    banner169: string | null;
    name: string | null;
    activePayoutToken: { [x: string]: any } | null;
    isClaimed: boolean | null;
    claimsLimit: number | null;
    claimCounter?: number | null;
    endDate: number | null;
    paused?: string | null;
  };
  link: string;
  onViewOfferClick: (name: string) => void;
  sharingControl?: ReactNode;
}

const HotOfferCard = ({ offer, onViewOfferClick, sharingControl, link }: HotOfferCardProps) => {
  const {
    banner169,
    name,
    isClaimed,
    claimsLimit,
    claimCounter,
    endDate,
    paused,
    activePayoutToken,
  } = offer;

  const isEnded = !!endDate && new Date() > new Date(endDate * 1000);
  const spotsLeft =
    claimsLimit && claimCounter
      ? Number(claimsLimit) - Number(claimCounter)
      : claimsLimit
      ? claimsLimit
      : undefined;

  const status = getStatus(isClaimed, isEnded, !!paused, claimsLimit, spotsLeft);

  const isViewButtonSecondary =
    status === 'claimed' || status === 'ended' || status === 'underReview' || status === 'paused';

  return (
    <Card className={styles['activity-card']} radius="large" padding="none">
      <div className={styles['activity-card__banner']}>
        <Image src={banner169 || ''} alt={`${name} Magic Store Hot Offer`} fill />
      </div>
      <div className={styles['activity-card__content']}>
        <div className={styles['activity-card__body']}>
          <ActivityStatus status={status} spotsLeft={spotsLeft} />
          <div className={styles['activity-card__name']}>{name}</div>
          <div className={styles['activity-card__badges']}>
            {activePayoutToken?.amount && (
              <TokenRewardChip
                offerAmount={activePayoutToken?.amount || 0}
                offerCurrency={activePayoutToken?.symbol || ''}
              />
            )}
          </div>
        </div>
        <div className={styles['activity-card__footer']}>
          <Link
            href={link}
            onClick={() => onViewOfferClick?.(name || '')}
            className={cn({
              [styles['primary']]: !isViewButtonSecondary,
              [styles['secondary']]: isViewButtonSecondary,
            })}
          >
            View Offer
          </Link>
          {sharingControl}
        </div>
      </div>
    </Card>
  );
};

export { HotOfferCard, type HotOfferCardProps };
